import { Box, Grid, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    createBuilding,
    deleteBuildingById,
    getAllBuilding,
    updateBuildingById,
} from "../../../../../api/buildingSettingApi";
import { swalCondition } from "../../../../../utils/swalHelper";
import {
    buildingTableColumns,
    initialBuildingTableOptions,
    initialBuildingValues,
} from "../helpers/AssignBuildEvtOption";
import { Modal, ModalBody } from "react-bootstrap";
import FormBuilding from "./FormBuilding";
import { useSelector } from "react-redux";

export default function AssignBuilding() {
    // ref
    const controllerRef = useRef(null);
    const tableRef = useRef(null);

    // global redux state
    const { thisMenuPermission } = useSelector(state => {
        const { menuPermission } = state?.menu;
        return {
            thisMenuPermission: menuPermission?.find(menu => menu?.path === window.location.pathname)?.permission,
        };
    });

    // component state
    const [tableOptions, setTableOptions] = useState(initialBuildingTableOptions);
    const [isShowModal, setIsShowModal] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(initialBuildingValues);

    // fetch data
    const fetchData = useCallback(async query => {
        try {
            const queryParams = {
                page: query?.page + 1 || 1,
                limit: query?.pageSize || 10,
                search: query?.search || "",
                order: query?.orderBy?.field || "buildingId",
                direction: query?.orderBy?.field ? query?.orderDirection : "desc",
            };
            const res = await getAllBuilding(queryParams, controllerRef.current?.signal);
            if (res) {
                setTableOptions(currentState => ({
                    ...currentState,
                    currentPage: query?.page,
                    pageSize: query?.pageSize || 10,
                    totalData: res?.pagination?.totalData || 0,
                    order: query?.orderBy?.field || "buildingId",
                    direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                }));
                return {
                    data: res?.data || [],
                    page: query?.page || 0,
                    totalCount: res?.pagination?.totalData || 0,
                };
            }
        } catch (error) {
            console.dir(error);
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
    }, []);

    // add building
    const handleAddBuilding = useCallback(async (values, { resetForm }) => {
        try {
            const isCreate = await createBuilding({ name: values?.buildingName }, controllerRef.current?.signal);
            if (isCreate) {
                resetForm();
                tableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // edit building
    const handleEditBuilding = useCallback(async values => {
        try {
            const isUpdate = await updateBuildingById(
                values?.buildingId,
                { name: values?.buildingName },
                controllerRef.current?.signal,
            );
            if (isUpdate) {
                setIsShowModal(false);
                tableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // delete building
    const handleDeleteBuilding = useCallback(async building => {
        try {
            const isConfirm = await swalCondition('<div className="h3 t-text-primary">Are you delete?</div>', "", {
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "YES",
                cancelButtonText: "NO",
            });
            if (isConfirm) {
                const isDelete = await deleteBuildingById(building?.buildingId, controllerRef.current?.signal);
                if (isDelete) {
                    tableRef.current.onQueryChange();
                }
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // open edit form modal
    const handleOpenEditFormModal = useCallback(async building => {
        setInitialFormValues({ buildingId: building?.buildingId, buildingName: building?.name });
        setIsShowModal(true);
    }, []);

    // close modal
    const handleCloseModal = useCallback(async () => {
        setIsShowModal(false);
        setInitialFormValues(initialBuildingValues);
    }, []);

    // run only first render
    useEffect(() => {
        controllerRef.current = new AbortController();
        // to avoid memory leaked.
        return () => {
            controllerRef.current.abort();
        };
    }, []);

    return (
        <Grid container spacing={0} className={"py-10"}>
            {/* form add */}
            {thisMenuPermission?.create && (
                <Grid item xs={12} className="mb-10">
                    <FormBuilding onSubmit={handleAddBuilding} initialFormValues={initialFormValues} isCreateForm />
                </Grid>
            )}

            {/* table */}
            <Grid item xs={12}>
                <div className="t-table-custom">
                    <MaterialTable
                        tableRef={tableRef}
                        components={{ Container: Box }}
                        title=""
                        data={fetchData}
                        options={tableOptions}
                        columns={buildingTableColumns}
                        actions={[
                            {
                                icon: "edit",
                                iconProps: { style: { fontSize: 18 }, className: "action-edit" },
                                tooltip: "Edit",
                                onClick: (event, rowData) => handleOpenEditFormModal(rowData),
                                hidden: !thisMenuPermission?.edit,
                            },
                            {
                                icon: "delete",
                                iconProps: { style: { fontSize: 18 }, className: "action-delete" },
                                tooltip: "Delete",
                                onClick: (event, rowData) => handleDeleteBuilding(rowData),
                                hidden: !thisMenuPermission?.delete,
                            },
                        ]}
                    />
                </div>
            </Grid>

            {/* modal */}
            <Grid item xs={12}>
                <Modal show={isShowModal} size="md" centered={true} scrollable={true} onHide={handleCloseModal}>
                    <Modal.Header>
                        <Modal.Title>Edit Building</Modal.Title>
                        <IconButton onClick={handleCloseModal} size="small">
                            <Close />
                        </IconButton>
                    </Modal.Header>
                    <ModalBody>
                        <FormBuilding
                            onSubmit={handleEditBuilding}
                            initialFormValues={initialFormValues}
                            isCreateForm={false}
                        />
                    </ModalBody>
                </Modal>
            </Grid>
        </Grid>
    );
}
